import React, { useRef, useState } from 'react';
import { EyeOff, Check, Plus, Minus, ScanEye } from 'lucide-react';

const MobileSwipeRow = ({ children, onToggleSelect, onExclude, isExcluded, isSelected }) => {
    // Current open/close state logic
    const [isOpen, setIsOpen] = useState(false);

    // Direct DOM manipulation refs for performance
    const cardRef = useRef(null);
    const startX = useRef(0);
    const currentX = useRef(0);
    const isDragging = useRef(false);

    // Positive = Swipe Right (Actions Reveal)
    const MAX_SWIPE = 160; // Increased for better button spacing
    const THRESHOLD = 50; // Pixels to snap open

    const onTouchStart = (e) => {
        startX.current = e.touches[0].clientX;
        currentX.current = isOpen ? MAX_SWIPE : 0;
        isDragging.current = true;

        if (cardRef.current) {
            cardRef.current.style.transition = 'none';
        }
    };

    const onTouchMove = (e) => {
        if (!isDragging.current) return;

        const touchX = e.touches[0].clientX;
        const diff = touchX - startX.current;

        // Calculate new position
        // If starting closed (0): diff needs to be positive (pulling right)
        let newX = isOpen ? MAX_SWIPE + diff : diff;

        // Resistance/Limits for Right Swipe
        if (newX < 0) newX = 0; // Can't swipe left past 0

        // Rubber band effect
        if (newX > MAX_SWIPE + 20) {
            newX = MAX_SWIPE + (newX - MAX_SWIPE) * 0.2;
        }

        currentX.current = newX;

        if (cardRef.current) {
            cardRef.current.style.transform = `translateX(${newX}px) ${isDragging.current ? 'scale(0.98)' : 'scale(1)'}`;
        }
    };

    const onTouchEnd = () => {
        if (!isDragging.current) return;
        isDragging.current = false;

        // Determine snap destination
        let finalX = 0;
        let shouldOpen = false;

        if (isOpen) {
            // Closing: verify if dragged back left enough
            shouldOpen = currentX.current > (MAX_SWIPE - THRESHOLD);
        } else {
            // Opening: verify if dragged right enough
            shouldOpen = currentX.current > THRESHOLD;
        }

        finalX = shouldOpen ? MAX_SWIPE : 0;
        setIsOpen(shouldOpen);

        if (cardRef.current) {
            // Spring-like bounce back
            cardRef.current.style.transition = 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
            cardRef.current.style.transform = `translateX(${finalX}px) scale(1)`;
        }
    };

    // Mouse handlers (for desktop testing)
    const onMouseDown = (e) => {
        startX.current = e.clientX;
        currentX.current = isOpen ? MAX_SWIPE : 0;
        isDragging.current = true;
        if (cardRef.current) cardRef.current.style.transition = 'none';
    };

    const onMouseMove = (e) => {
        if (!isDragging.current) return;
        const diff = e.clientX - startX.current;
        let newX = isOpen ? MAX_SWIPE + diff : diff;
        if (newX < 0) newX = 0;
        if (newX > MAX_SWIPE + 20) newX = MAX_SWIPE + (newX - MAX_SWIPE) * 0.2;
        currentX.current = newX;
        if (cardRef.current) cardRef.current.style.transform = `translateX(${newX}px)`;
    };

    const onMouseUp = () => {
        if (!isDragging.current) return;
        onTouchEnd();
    };

    const reset = () => {
        setIsOpen(false);
        if (cardRef.current) {
            cardRef.current.style.transition = 'transform 0.3s ease';
            cardRef.current.style.transform = 'translateX(0px)';
        }
    };

    return (
        <div className="relative mb-3 rounded-xl overflow-visible select-none touch-pan-y">
            {/* Background Actions - PINNED TO LEFT (Revealed on Right Swipe) */}
            <div className={`absolute inset-y-0 left-0 w-full flex items-center justify-start pl-4 gap-4 rounded-xl transition-colors ${isSelected ? 'bg-emerald-50' : 'bg-slate-100'}`} dir="ltr">

                {/* Select Button */}
                <div className="flex flex-col items-center gap-1">
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleSelect(); reset(); }}
                        className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all z-10 
                            ${isSelected
                                ? 'bg-emerald-500 text-white ring-4 ring-emerald-200'
                                : 'bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                    >
                        {isSelected ? <Check size={24} strokeWidth={3} /> : <Plus size={24} />}
                    </button>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{isSelected ? 'נבחר' : 'בחר'}</span>
                </div>

                {/* Exclude Button */}
                <div className="flex flex-col items-center gap-1">
                    <button
                        onClick={(e) => { e.stopPropagation(); onExclude(); reset(); }}
                        className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all z-10
                             ${isExcluded
                                ? 'bg-slate-800 text-white'
                                : 'bg-white text-slate-400 hover:text-red-500'
                            }`}
                    >
                        {isExcluded ? <ScanEye size={24} /> : <EyeOff size={24} />}
                    </button>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{isExcluded ? 'החזר' : 'הסתר'}</span>
                </div>
            </div>

            {/* Foreground Card */}
            <div
                ref={cardRef}
                className="relative z-20 cursor-grab active:cursor-grabbing transform-gpu will-change-transform"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onClick={(e) => {
                    if (isOpen) {
                        e.stopPropagation();
                        reset();
                    }
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default MobileSwipeRow;
